import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiBox,
  MiDatePicker,
  MiDetailFields,
  MiIcon,
  MiSectionHeader,
} from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  ENTITY_NAMES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { toFormattedDate } from "@miplatform/utils";
import { mdiPencilOutline, mdiPlusCircleOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { CreateBidRequestFormData } from "../CreateBidRequest.type";
import { OPTION } from "../constants/constants";
import { RequesterField } from "./RequesterField";
import { useRequestDetailsSectionViewModel } from "./useRequestDetailsSectionViewModel";

const NONE_OPTION = [{ name: "None", id: null }];
const NONE_ASSIGNEE = [{ firstName: "None", lastName: "", id: null }];

export function RequestDetailsSection({
  form,
  selectedOption,
  setSelectedOption,
  isCreatingContact,
  setIsCreatingContact,
}: Readonly<RequestDetailsSectionProps>) {
  const [city, setCity] = useState("");

  const {
    accounts,
    seriesGroups,
    series,
    communities,
    contacts,
    departments,
    getAllData,
    fetchContacts,
    fetchSeriesGroups,
    fetchSeries,
    addContact,
  } = useRequestDetailsSectionViewModel();

  const { control, setValue, watch } = form;
  const { dueDate, requestDate, requesterId, builderId, seriesGroupId } =
    watch();
  const { classes, theme, cx } = useStyles();

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    if (builderId) {
      setValue("requesterId", "", { shouldDirty: true, shouldValidate: true });
      fetchContacts(builderId);
      fetchSeriesGroups(builderId);
    }
  }, [builderId]);

  useEffect(() => {
    if (builderId) {
      fetchSeries(builderId, seriesGroupId || "");
    }
  }, [builderId, seriesGroupId]);

  const optionConfigs = [
    {
      id: OPTION.NEW_BID,
      title: OPTION.NEW_BID,
      icon: mdiPlusCircleOutline,
      onClick: () => setSelectedOption(OPTION.NEW_BID),
    },
    {
      id: OPTION.UPDATE_BID,
      title: OPTION.UPDATE_BID,
      icon: mdiPencilOutline,
      onClick: () => setSelectedOption(OPTION.UPDATE_BID),
    },
  ];

  const options = optionConfigs.map((option) => {
    const isSelected = selectedOption === option.id;

    return {
      name: (
        <Box className={classes.optionBox}>
          <MiIcon
            path={option.icon}
            size={0.8}
            color={
              isSelected
                ? theme.palette.primary.main
                : theme.palette.grey.medium
            }
          />
          <Typography className={classes.optionTitle}>
            {option.title}
          </Typography>
        </Box>
      ),
      onClick: option.onClick,
      className: cx(classes.actionBtn, isSelected && classes.activeOption),
    };
  });

  const requestFields = [
    {
      label: "Request Name",
      name: "name",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.TEXTBOX,
    },
    {
      label: "Customer",
      name: "builderId",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: accounts.map((a) => ({ label: a.name, value: a.id })),
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("builderId", e.target.value, { shouldValidate: true });
        setValue("seriesGroupId", null, { shouldDirty: true });
        setValue("seriesId", null, { shouldDirty: true });
      },
    },
    {
      label: "Community",
      name: "communityId",
      width: "50%",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: [...NONE_OPTION, ...communities].map((c) => ({
        label: c.name,
        value: c.id,
      })),
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        const community = communities.find((c) => c.id === e.target.value);
        setValue("communityId", e.target.value, { shouldDirty: true });
        setCity(community?.city?.cityName ?? "");
      },
    },
    {
      label: "City",
      value: city,
      width: "50%",
      disabled: true,
    },
    {
      label: "Request Date",
      name: "requestDate",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <MiDatePicker
          isFullWidth
          disablePast
          readOnly
          value={requestDate}
          onChange={(date: Date) => {
            const formattedDate = toFormattedDate(
              date,
              DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
            );
            setValue("requestDate", formattedDate, { shouldValidate: true });
          }}
        />
      ),
    },
    {
      label: "Due Date",
      name: "dueDate",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <MiDatePicker
          isFullWidth
          disablePast
          readOnly
          value={dueDate}
          onChange={(date: Date) => {
            const formattedDate = toFormattedDate(
              date,
              DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
            );
            setValue("dueDate", formattedDate, { shouldValidate: true });
          }}
        />
      ),
    },
    {
      label: "Requester",
      name: "requesterId",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <RequesterField
          value={requesterId}
          contacts={contacts}
          builderId={builderId}
          required
          isCreatingContact={isCreatingContact}
          setIsCreatingContact={setIsCreatingContact}
          onChange={(contactId: string) =>
            setValue("requesterId", contactId, { shouldValidate: true })
          }
          onContactCreated={(contact) => {
            addContact(contact);
            setValue("requesterId", contact.id, { shouldValidate: true });
          }}
        />
      ),
    },
    {
      label: "Assignee",
      name: "assigneeId",
      width: "50%",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: [...NONE_ASSIGNEE, ...departments.flatMap((d) => d.users)].map(
        (u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u.id,
        })
      ),
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: <div />,
    } as any,
  ];

  const seriesField = [
    {
      label: "Series Group",
      name: "seriesGroupId",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      options: [...NONE_OPTION, ...seriesGroups].map((s) => ({
        label: s.name,
        value: s.id,
      })),
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("seriesGroupId", e.target.value, { shouldDirty: true });
        setValue("seriesId", null, { shouldDirty: true });
      },
    },
    {
      label: "Series",
      name: "seriesId",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      options: [...NONE_OPTION, ...series].map((s) => ({
        label: s.name,
        value: s.id,
      })),
    },
  ];

  return (
    <MiBox
      headerTitle={`${ENTITY_NAMES.BID_REQUEST} Details`}
      sx={{ overflow: "visible" }}
    >
      <MiSectionHeader
        title="Request Type"
        variant="text"
        headerStyle={{ padding: theme.spacing(0, 0, 0, 1) }}
      />
      <Box className={classes.actionBox}>
        <MiActions actions={options} />
      </Box>
      <MiDetailFields
        detailFields={requestFields}
        headerProps={{
          title: "Request Details",
          variant: "text",
          style: { padding: theme.spacing(0, 0, 0, 1) },
        }}
      />
      <MiDetailFields
        detailFields={seriesField}
        headerProps={{
          title: "Series",
          variant: "text",
          style: { padding: theme.spacing(0, 0, 0, 1) },
        }}
      />
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  optionBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  optionTitle: {
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.common.black,
    fontSize: theme.fontSize.medium,
  },
  actionBox: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  actionBtn: {
    border: `1px solid ${theme.palette.grey.accent}`,
    width: "100%",
    backgroundColor: theme.palette.grey.light,
  },
  activeOption: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light,
  },
}));

interface RequestDetailsSectionProps {
  form: UseFormReturn<CreateBidRequestFormData>;
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  isCreatingContact: boolean;
  setIsCreatingContact: (isCreating: boolean) => void;
}
